/**
 * Опції порталу (тенанта): коди, значення за замовчуванням і нормалізація
 * для порівняння «є незбережені зміни» на сторінці «Налаштування порталу».
 */
export const OPTION_DELAY_ESCALATION_MINUTES = 'delay_escalation_minutes'
export const OPTION_ESCALATION_EMAILS = 'escalation_emails'

/** Значення у формі: email одним рядком, як їх вводить користувач */
export interface PortalOptionsForm {
    [OPTION_DELAY_ESCALATION_MINUTES]: number
    [OPTION_ESCALATION_EMAILS]: string
}

/** Значення для API і порівняння: email масивом */
export interface PortalOptionsNormalized {
    [OPTION_DELAY_ESCALATION_MINUTES]: number
    [OPTION_ESCALATION_EMAILS]: string[]
}

export const DEFAULT_PORTAL_OPTIONS: PortalOptionsForm = {
    [OPTION_DELAY_ESCALATION_MINUTES]: 120,
    [OPTION_ESCALATION_EMAILS]: '',
}

/** Email через кому, крапку з комою або новий рядок → масив без порожніх */
export function splitEmails(raw: string | undefined): string[] {
    return (raw ?? '')
        .split(/[\n,;]+/)
        .map((e) => e.trim())
        .filter(Boolean)
}

export function normalizePortalOptions(values: Partial<PortalOptionsForm>): PortalOptionsNormalized {
    return {
        [OPTION_DELAY_ESCALATION_MINUTES]: Number(values[OPTION_DELAY_ESCALATION_MINUTES]) || 0,
        [OPTION_ESCALATION_EMAILS]: splitEmails(values[OPTION_ESCALATION_EMAILS]),
    }
}

export function isEqualPortalOptions(a: Partial<PortalOptionsForm>, b: Partial<PortalOptionsForm>): boolean {
    const na = normalizePortalOptions(a)
    const nb = normalizePortalOptions(b)
    return (
        na[OPTION_DELAY_ESCALATION_MINUTES] === nb[OPTION_DELAY_ESCALATION_MINUTES] &&
        na[OPTION_ESCALATION_EMAILS].join('\n') === nb[OPTION_ESCALATION_EMAILS].join('\n')
    )
}

/** Контроль пробігу: вмикач і пороги відхилення (backend MileageDeviationEvaluator) */
export const OPTION_MILEAGE_CONTROL_ENABLED = 'mileage_control_enabled'
export const OPTION_MILEAGE_DEVIATION_PERCENT = 'mileage_deviation_percent'
export const OPTION_MILEAGE_DEVIATION_KM = 'mileage_deviation_km'

export interface MileageOptionsForm {
    [OPTION_MILEAGE_CONTROL_ENABLED]: boolean
    [OPTION_MILEAGE_DEVIATION_PERCENT]: number
    [OPTION_MILEAGE_DEVIATION_KM]: number
}

export const DEFAULT_MILEAGE_OPTIONS: MileageOptionsForm = {
    [OPTION_MILEAGE_CONTROL_ENABLED]: true,
    [OPTION_MILEAGE_DEVIATION_PERCENT]: 10,
    [OPTION_MILEAGE_DEVIATION_KM]: 20,
}

/** Опції тенанта → пороги; відсутнє чи некоректне значення — як у бекенда, за замовчуванням */
export function readMileageOptions(items: Array<{ code?: string; value?: unknown }>): MileageOptionsForm {
    const result = { ...DEFAULT_MILEAGE_OPTIONS }
    items.forEach((item) => {
        // Вимкнено лише явним false (або «false»/0) — як на бекенді
        if (item.code === OPTION_MILEAGE_CONTROL_ENABLED) {
            result[OPTION_MILEAGE_CONTROL_ENABLED] = ![false, 0, '0', 'false'].includes(item.value as never)
            return
        }
        if (item.code !== OPTION_MILEAGE_DEVIATION_PERCENT && item.code !== OPTION_MILEAGE_DEVIATION_KM) return
        const value = typeof item.value === 'number' || typeof item.value === 'string' ? Number(item.value) : NaN
        if (item.value === '' || !Number.isFinite(value) || value < 0) return
        result[item.code] = item.code === OPTION_MILEAGE_DEVIATION_KM ? Math.trunc(value) : value
    })
    return result
}

/** Нагадування водієві завершити зміну (backend ShiftEndReminderSettings) */
export const OPTION_SHIFT_REMINDER_ENABLED = 'shift_end_reminder_enabled'
export const OPTION_SHIFT_REMINDER_TIME = 'shift_end_reminder_time'
export const OPTION_SHIFT_REMINDER_MIN_HOURS = 'shift_end_reminder_min_hours'

/** Як на бекенді: мінімальна тривалість зміни — ціле 0…24 год */
export const MAX_SHIFT_REMINDER_MIN_HOURS = 24

export interface ShiftReminderOptionsForm {
    [OPTION_SHIFT_REMINDER_ENABLED]: boolean
    /** «HH:mm» за київським часом */
    [OPTION_SHIFT_REMINDER_TIME]: string
    [OPTION_SHIFT_REMINDER_MIN_HOURS]: number
}

export const DEFAULT_SHIFT_REMINDER_OPTIONS: ShiftReminderOptionsForm = {
    [OPTION_SHIFT_REMINDER_ENABLED]: false,
    [OPTION_SHIFT_REMINDER_TIME]: '20:00',
    [OPTION_SHIFT_REMINDER_MIN_HOURS]: 4,
}

/** «H:mm» або «HH:mm» у межах доби → «HH:mm»; інше — null */
export function parseReminderTime(value: unknown): string | null {
    if (typeof value !== 'string') return null
    const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value.trim())
    return match?.[1] && match[2] ? `${match[1].padStart(2, '0')}:${match[2]}` : null
}

/** Опції тенанта → форма; відсутнє чи некоректне значення — як у бекенда, за замовчуванням */
export function readShiftReminderOptions(items: Array<{ code?: string; value?: unknown }>): ShiftReminderOptionsForm {
    const result = { ...DEFAULT_SHIFT_REMINDER_OPTIONS }
    items.forEach((item) => {
        if (item.code === OPTION_SHIFT_REMINDER_ENABLED) {
            // Увімкнено лише явним true (або «true»/1) — як на бекенді
            result[OPTION_SHIFT_REMINDER_ENABLED] = [true, 1, '1', 'true'].includes(item.value as never)
        } else if (item.code === OPTION_SHIFT_REMINDER_TIME) {
            result[OPTION_SHIFT_REMINDER_TIME] =
                parseReminderTime(item.value) ?? DEFAULT_SHIFT_REMINDER_OPTIONS[OPTION_SHIFT_REMINDER_TIME]
        } else if (item.code === OPTION_SHIFT_REMINDER_MIN_HOURS) {
            // Як на бекенді: ціле число або рядок із цифр
            const value =
                typeof item.value === 'number'
                    ? item.value
                    : typeof item.value === 'string' && /^\d+$/.test(item.value.trim())
                      ? Number(item.value.trim())
                      : NaN
            if (Number.isInteger(value) && value >= 0 && value <= MAX_SHIFT_REMINDER_MIN_HOURS) {
                result[OPTION_SHIFT_REMINDER_MIN_HOURS] = value
            }
        }
    })
    return result
}
